import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FaSearch,
  FaBullhorn,
  FaSyncAlt,
  FaExternalLinkAlt,
  FaCalendarAlt,
  FaNewspaper,
  FaPlus,
  FaTrashAlt,
  FaSave,
  FaTimes,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  Grid,
  Span,
  TechInput,
  TechTextarea,
  TechButton,
  TechButtonGhost,
  StatusPill,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  TechChip,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

import { listRecentNews, getCompetitorNews } from '../../services/competitorsService';
import { listSpeeches, createSpeech, deleteSpeech } from '../../services/platformService';

const OWN_SLUG = 'federico-chouhy-lla';

const SearchRow = styled.div`
  display: flex;
  gap: 0.55rem;
  margin-bottom: 1.25rem;

  @media (max-width: 768px) { flex-direction: column; }
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;

  > svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-disabled);
    font-size: 0.85rem;
  }
  input { padding-left: 2rem; }
`;

const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.55rem;
  padding: 0.6rem 0.85rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 5px;

  &:hover { border-color: rgba(0, 180, 216, 0.3); background: rgba(0, 180, 216, 0.04); }

  a {
    color: var(--text-primary);
    text-decoration: none;
    display: inline-flex;
    align-items: flex-start;
    gap: 6px;
    line-height: 1.4;

    svg { color: var(--text-disabled); font-size: 0.75rem; margin-top: 4px; flex-shrink: 0; }
    &:hover { color: ${accent}; }
  }

  .meta {
    font-family: ${mono};
    font-size: 0.65rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-disabled);
    align-self: center;
    white-space: nowrap;
  }
`;

const KeywordChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.55rem;
  font-family: ${mono};
  font-size: 0.7rem;
  background: rgba(0, 180, 216, 0.08);
  border: 1px solid rgba(0, 180, 216, 0.25);
  color: #cfe9f4;
  border-radius: 3px;

  &::before { content: '#'; color: ${accent}; opacity: 0.85; }

  .count {
    font-size: 0.62rem;
    color: ${accent};
  }
`;

const STOP = new Set(['para', 'sobre', 'esta', 'este', 'sus', 'una', 'uno', 'con', 'que', 'los', 'las', 'del', 'desde', 'cuando', 'donde', 'sino', 'porque', 'pero', 'como', 'todos', 'todas', 'todo', 'toda', 'también', 'ante', 'tras', 'fueron', 'fue', 'son', 'entre', 'dijo', 'según', 'buenos', 'aires']);

function extractKeywords(news, limit = 12) {
  const counts = new Map();
  for (const n of news) {
    const text = `${n.title || ''} ${n.summary || ''}`.toLowerCase();
    const tokens = text
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .split(/[^a-z0-9]+/i)
      .filter((t) => t.length >= 5 && !STOP.has(t));
    for (const t of tokens) counts.set(t, (counts.get(t) || 0) + 1);
  }
  return [...counts.entries()].sort(([, a], [, b]) => b - a).slice(0, limit);
}

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const h = diff / 36e5;
  if (h < 1) return 'hace <1h';
  if (h < 24) return `hace ${Math.round(h)}h`;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

const PoliticalDiscourse = () => {
  const [search, setSearch] = useState('');
  const [ownNews, setOwnNews] = useState({ news: [], competitor: null });
  const [allNews, setAllNews] = useState([]);
  const [speeches, setSpeeches] = useState([]);
  const [loadState, setLoadState] = useState({ loading: true, error: null });

  // editor de discursos
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState({ title: '', date: '', place: '', duration_min: '', content: '', tags: '' });
  const [busy, setBusy] = useState(false);

  const reload = () => {
    let cancelled = false;
    setLoadState({ loading: true, error: null });
    Promise.all([
      getCompetitorNews(OWN_SLUG, 50).catch(() => ({ news: [], competitor: null })),
      listRecentNews({ limit: 100 }).catch(() => ({ news: [] })),
      listSpeeches().catch(() => []),
    ])
      .then(([own, all, sp]) => {
        if (cancelled) return;
        setOwnNews(own || { news: [], competitor: null });
        setAllNews(all?.news || []);
        setSpeeches(sp || []);
        setLoadState({ loading: false, error: null });
      })
      .catch((e) => {
        if (!cancelled) setLoadState({ loading: false, error: e?.message || 'Error' });
      });
    return () => { cancelled = true; };
  };

  useEffect(reload, []);

  const handleSpeechSubmit = async (e) => {
    e.preventDefault();
    if (!draft.title.trim() || !draft.date) return;
    setBusy(true);
    try {
      await createSpeech({
        title: draft.title.trim(),
        date: draft.date,
        place: draft.place || null,
        duration_min: draft.duration_min ? Number(draft.duration_min) : null,
        content: draft.content || null,
        tags: draft.tags ? draft.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      });
      setDraft({ title: '', date: '', place: '', duration_min: '', content: '', tags: '' });
      setEditorOpen(false);
      reload();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const handleSpeechDelete = async (id) => {
    if (!window.confirm('¿Eliminar discurso?')) return;
    try {
      await deleteSpeech(id);
      reload();
    } catch (e) {
      alert(e?.message || 'Error');
    }
  };

  const filtered = useMemo(() => {
    if (!search) return ownNews.news;
    const q = search.toLowerCase();
    return ownNews.news.filter((n) =>
      (n.title || '').toLowerCase().includes(q) ||
      (n.source || '').toLowerCase().includes(q)
    );
  }, [ownNews.news, search]);

  const keywords = useMemo(() => extractKeywords(ownNews.news, 14), [ownNews.news]);

  if (loadState.loading) {
    return (
      <TechShell>
        <TechPageHeader title="Discurso Político" dossier="DSC://VOICE/01" />
        <Loading>Cargando menciones reales</Loading>
      </TechShell>
    );
  }
  if (loadState.error) {
    return (
      <TechShell>
        <TechPageHeader title="Discurso Político" dossier="DSC://VOICE/01" />
        <ErrorBox>No se pudo cargar · {loadState.error}</ErrorBox>
      </TechShell>
    );
  }

  return (
    <TechShell>
      <TechPageHeader
        title="Discurso Político"
        subtitle="Análisis de menciones, palabras clave y cobertura del candidato propio"
        dossier="DSC://VOICE/01"
        actions={<TechButtonGhost type="button" onClick={() => window.location.reload()}><FaSyncAlt /> Refrescar</TechButtonGhost>}
      >
        <StatusPill>Menciones <strong>{ownNews.news.length}</strong></StatusPill>
        <StatusPill>Palabras clave <strong>{keywords.length}</strong></StatusPill>
        <StatusPill>Última actualización <strong>{fmtRel(ownNews.competitor?.last_scraped_at)}</strong></StatusPill>
      </TechPageHeader>

      <StatStrip min="180px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaBullhorn /> Menciones del candidato</StatLabel>
          <StatValue>{ownNews.news.length}</StatValue>
          <StatSub>indexadas</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaNewspaper /> En el feed general</StatLabel>
          <StatValue>{allNews.length}</StatValue>
          <StatSub>últimas 100</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel>Top fuentes</StatLabel>
          <StatValue style={{ fontSize: '0.95rem' }}>
            {(() => {
              const m = {};
              for (const n of ownNews.news) m[n.source || 'sin fuente'] = (m[n.source || 'sin fuente'] || 0) + 1;
              const top = Object.entries(m).sort(([, a], [, b]) => b - a)[0];
              return top ? `${top[0]}` : '—';
            })()}
          </StatValue>
          <StatSub>fuente más activa</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaCalendarAlt /> Última mención</StatLabel>
          <StatValue style={{ fontSize: '0.95rem' }}>
            {fmtRel(ownNews.news[0]?.published_at || ownNews.news[0]?.fetched_at)}
          </StatValue>
        </StatTile>
      </StatStrip>

      <SearchRow>
        <SearchWrap>
          <FaSearch />
          <TechInput
            placeholder="Buscar mención o medio…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchWrap>
      </SearchRow>

      <Grid>
        <Span span={8} className="mobile-full-width">
          <TechSection title={`Menciones del candidato propio · ${filtered.length}`}>
            {filtered.length === 0 ? (
              <Empty>
                Sin menciones del candidato propio para los filtros activos.
                {ownNews.news.length === 0 && ' El próximo monitoreo puede sumar nuevas.'}
              </Empty>
            ) : (
              <ItemList>
                {filtered.map((n) => (
                  <Row key={n.id}>
                    <a href={n.url} target="_blank" rel="noopener noreferrer">
                      {n.title} <FaExternalLinkAlt />
                    </a>
                    <span className="meta">
                      <span>{n.source}</span> · {fmtRel(n.published_at || n.fetched_at)}
                    </span>
                  </Row>
                ))}
              </ItemList>
            )}
          </TechSection>
        </Span>

        <Span span={4} className="mobile-full-width">
          <TechSection title={`Palabras clave · top ${keywords.length}`}>
            {keywords.length === 0 ? (
              <Empty>Sin keywords extraíbles del feed actual.</Empty>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {keywords.map(([term, count]) => (
                  <KeywordChip key={term}>
                    {term} <span className="count">{count}</span>
                  </KeywordChip>
                ))}
              </div>
            )}
            <p style={{ marginTop: 12, fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
              Extraídas de títulos + resúmenes · {ownNews.news.length} menciones analizadas
            </p>
          </TechSection>

          <TechSection
            title={`Discursos publicados · ${speeches.length}`}
            action={
              <TechButton type="button" onClick={() => setEditorOpen(!editorOpen)}>
                {editorOpen ? <FaTimes /> : <FaPlus />}
                {editorOpen ? 'Cerrar' : 'Cargar discurso'}
              </TechButton>
            }
            style={{ marginTop: '1.25rem' }}
          >
            {editorOpen && (
              <form onSubmit={handleSpeechSubmit} style={{ marginBottom: 12, padding: 12, background: panelDeep, border: `1px solid ${borderSoft}`, borderRadius: 6 }}>
                <TechInput
                  placeholder="Título del discurso *"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  style={{ marginBottom: 8 }}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <TechInput type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
                  <TechInput placeholder="Lugar" value={draft.place} onChange={(e) => setDraft({ ...draft, place: e.target.value })} />
                  <TechInput type="number" placeholder="Duración (min)" value={draft.duration_min} onChange={(e) => setDraft({ ...draft, duration_min: e.target.value })} />
                </div>
                <TechInput
                  placeholder="Tags (separados por coma)"
                  value={draft.tags}
                  onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
                  style={{ marginBottom: 8 }}
                />
                <TechTextarea
                  placeholder="Contenido del discurso"
                  rows={4}
                  value={draft.content}
                  onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                  style={{ marginBottom: 8 }}
                />
                <TechButton type="submit" disabled={busy}>
                  <FaSave /> {busy ? 'Guardando…' : 'Guardar discurso'}
                </TechButton>
              </form>
            )}

            {speeches.length === 0 ? (
              <Empty>
                Aún no se cargaron discursos en la base. Tocá <strong>Cargar discurso</strong> para sumar el primero.
              </Empty>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {speeches.map((sp) => (
                  <article
                    key={sp.id}
                    style={{
                      padding: '0.7rem 0.85rem',
                      background: panelDeep,
                      border: `1px solid ${borderSoft}`,
                      borderLeft: `2px solid ${accent}`,
                      borderRadius: 5,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h5 style={{ margin: '0 0 4px', color: 'var(--text-primary)', fontSize: '0.92rem' }}>{sp.title}</h5>
                        <div style={{ fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-disabled)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span><FaCalendarAlt style={{ marginRight: 3, fontSize: '0.7rem', color: accent }} /> {sp.date?.slice(0, 10)}</span>
                          {sp.place && <span>· {sp.place}</span>}
                          {sp.duration_min && <span>· {sp.duration_min} min</span>}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleSpeechDelete(sp.id)}
                        style={{
                          background: 'transparent',
                          border: `1px solid ${borderSoft}`,
                          color: 'var(--text-disabled)',
                          width: 26,
                          height: 26,
                          borderRadius: 5,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FaTrashAlt style={{ fontSize: '0.72rem' }} />
                      </button>
                    </div>
                    {sp.content && (
                      <p style={{ margin: '6px 0 0', color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.5 }}>
                        {sp.content}
                      </p>
                    )}
                    {Array.isArray(sp.tags) && sp.tags.length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {sp.tags.map((t) => <TechChip key={t} tone="mute" style={{ fontSize: '0.62rem' }}>#{t}</TechChip>)}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </TechSection>
        </Span>
      </Grid>
    </TechShell>
  );
};

export default PoliticalDiscourse;
